import { useState } from 'react'
import { createPortal } from 'react-dom'
import { ButtonClose, ButtonPopup } from '../ui'
import { Horario, PopupProps, AlertaType } from '@/types'
import { PopupStilosCss } from '../styles'
import Alerta from '../alert/Alerta'

interface PopupHorariosProps extends PopupProps {
    initialHorarios: Horario[]
    onClose: () => void
    onConfirm: (horarios: Horario[]) => void
}

const diasSemana = [
    'Domingo',
    'Lunes',
    'Martes',
    'Miércoles',
    'Jueves',
    'Viernes',
    'Sábado',
]

interface HorarioState {
    horario_id?: number
    dia_semana: number
    hora_inicio: string
    hora_fin: string
}

const PopupHorarios: React.FC<PopupHorariosProps> = ({
    initialHorarios,
    onClose,
    onConfirm,
    className,
}) => {
    const [horarios, setHorarios] = useState<HorarioState[]>(
        initialHorarios.map(h => ({
            horario_id: h.horario_id,
            dia_semana: h.dia_semana,
            hora_inicio: h.hora_inicio,
            hora_fin: h.hora_fin,
        }))
    )

    // 🔔 ALERTAS
    const [showAlert, setShowAlert] = useState(false)
    const [alertMensaje, setAlertMensaje] = useState('')
    const [alertType, setAlertType] = useState<AlertaType>('CambiosRealizados')

    const mostrarAlerta = (
        mensaje: string,
        type: AlertaType = 'CambiosRealizados'
    ) => {
        setAlertMensaje(mensaje)
        setAlertType(type)
        setShowAlert(true)
    }

    // 🧠 Detectar cambios
    const hayCambios = () => {
        if (horarios.length !== initialHorarios.length) return true

        return horarios.some((h, i) => {
            const o = initialHorarios[i]
            if (!o) return true

            return (
                h.dia_semana !== o.dia_semana ||
                h.hora_inicio !== o.hora_inicio ||
                h.hora_fin !== o.hora_fin
            )
        })
    }

    const horariosPorDia = diasSemana.map((_, dia) => ({
        dia,
        tramos: horarios.filter(h => h.dia_semana === dia),
    }))

    const addTramo = (dia: number) => {
        setHorarios(prev => [
            ...prev,
            { dia_semana: dia, hora_inicio: '', hora_fin: '' },
        ])
    }

    const updateTramo = (
        index: number,
        field: 'hora_inicio' | 'hora_fin',
        value: string
    ) => {
        setHorarios(prev =>
            prev.map((h, i) => (i === index ? { ...h, [field]: value } : h))
        )
    }

    const removeTramo = (index: number) => {
        setHorarios(prev => prev.filter((_, i) => i !== index))
    }

    // 🧠 Validar solapamientos
    const haySolapamientos = () => {
        const porDia: Record<number, HorarioState[]> = {}

        for (const h of horarios) {
            if (!porDia[h.dia_semana]) porDia[h.dia_semana] = []
            porDia[h.dia_semana].push(h)
        }

        for (const dia in porDia) {
            const tramos = porDia[+dia]
            for (let i = 0; i < tramos.length; i++) {
                for (let j = i + 1; j < tramos.length; j++) {
                    const a = tramos[i]
                    const b = tramos[j]
                    if (
                        a.hora_inicio < b.hora_fin &&
                        b.hora_inicio < a.hora_fin
                    ) {
                        return diasSemana[+dia]
                    }
                }
            }
        }
        return null
    }

    const handleConfirm = () => {
        if (!hayCambios()) {
            mostrarAlerta('No se ha modificado ningún horario')
            return
        }

        for (const h of horarios) {
            if (!h.hora_inicio || !h.hora_fin || h.hora_inicio >= h.hora_fin) {
                mostrarAlerta(`Horario inválido en ${diasSemana[h.dia_semana]}`, 'noFirma')
                return
            }
        }

        const diaSolapado = haySolapamientos()
        if (diaSolapado) {
            mostrarAlerta(`Horarios solapados en ${diaSolapado}`, 'noFirma')
            return
        }

        const horariosParaGuardar: Horario[] = horarios.map(h => ({
            horario_id: h.horario_id,
            dia_semana: h.dia_semana,
            hora_inicio: h.hora_inicio,
            hora_fin: h.hora_fin,
            activo: true,
        }))

        onConfirm(horariosParaGuardar)
        onClose()
    }

    return createPortal(
        <>
            {showAlert && (
                <Alerta
                    mensaje={alertMensaje}
                    type={alertType}
                    onClose={() => setShowAlert(false)}
                />
            )}

            <div className={`fixed inset-0 ${PopupStilosCss.popupTelefono} flex items-center justify-center bg-black bg-opacity-50 z-[9999] text-zinc-700`} >
                <div className={`${className || ''} space-y-6 bg-zinc-100 shadow-xl rounded-md p-8 relative ${PopupStilosCss.popupTelefono} mx-4 overflow-auto h-96 xs:w-full md:w-3/12 scrollbar-custom`} >

                    {/* Botón de cerrar en la esquina superior izquierda */}
                    <ButtonClose
                        onClick={onClose}
                        className="absolute top-2 left-2 text-zinc-600 hover:text-red-600 font-bold text-xl"
                        aria-label="Cerrar"
                    />


                    <h2 className="text-xl font-bold text-center">Editar Horarios</h2>

                    <div className="grid grid-cols-1 gap-4">
                        {horariosPorDia.map(({ dia, tramos }) => (
                            <div key={dia} className="space-y-2">
                                <h3 className="font-semibold">{diasSemana[dia]}</h3>

                                {tramos.map(tramo => {
                                    const index = horarios.indexOf(tramo)
                                    return (
                                        <div key={index} className="flex gap-2 items-center text-zinc-700">
                                            <input
                                                type="time"
                                                value={tramo.hora_inicio}
                                                onChange={e => updateTramo(index, 'hora_inicio', e.target.value)}
                                                className="border-r-2 border-b-2 border border-blue-700 rounded-md shadow-md pl-1 text-zinc-700 dark:text-white bg-white dark:bg-zinc-800"
                                            />
                                            —
                                            <input
                                                type="time"
                                                value={tramo.hora_fin}
                                                onChange={e => updateTramo(index, 'hora_fin', e.target.value)}
                                                className="border-r-2 border-b-2 border border-blue-700 rounded-md shadow-md pl-1 text-zinc-700 dark:text-white bg-white dark:bg-zinc-800"
                                            />
                                            <ButtonClose
                                                onClick={() => removeTramo(index)}
                                                className="text-red-600 font-bold"
                                            >
                                                
                                            </ButtonClose>
                                        </div>
                                    )
                                })}

                                <button
                                    onClick={() => addTramo(dia)}
                                    className="text-sm text-blue-700 underline"
                                >
                                    + Añadir tramo
                                </button>
                            </div>
                        ))}
                    </div>

                    <div className="flex gap-2 pt-4">
                        <ButtonPopup onClick={handleConfirm} className="w-1/2">
                            Guardar
                        </ButtonPopup>
                        <ButtonPopup onClick={onClose} className="w-1/2">
                            Cancelar
                        </ButtonPopup>
                    </div>
                </div>
            </div>
        </>,
        document.body
    )
}

export default PopupHorarios
